import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ManageableProduct, ProductFilters, useProducts } from '../products';

type ProductFiltersWatch = Pick<
  ProductFilters,
  'names' | 'matches' | 'pricebookTypes' | 'businessUnitIds' | 'categoryIds'
>;

type PageParams =
  | 'groupingName'
  | 'defaultSearchTerm'
  | 'matches'
  | 'businessUnitId'
  | 'pricebookTypes'
  | 'parentCategoryId';

const splitAndSort = <T extends string>(param?: string, separator = ',') =>
  (param
    ? param
        .split(separator)
        .map((item) => item.trim())
        .filter(Boolean)
        .sort()
    : []) as T[];

const isFilterValid = (options: ProductFiltersWatch) =>
  Object.values(options).some((d) => d.length);

const paramsChecker =
  (params: URLSearchParams) =>
  (paramName: PageParams, filter: string[], ignoreDelete: boolean) => {
    const currentParamValue = params.get(paramName) || '';
    const hasParam = params.has(paramName);
    const currentFilter = filter
      .map((d) => d.trim())
      .filter(Boolean)
      .sort()
      .join(',');

    if (
      currentFilter &&
      (!hasParam || (hasParam && currentFilter !== currentParamValue))
    ) {
      params.set(paramName, currentFilter);
    } else if (hasParam && !currentFilter && !ignoreDelete) {
      params.delete(paramName);
    }
  };

const paramsBuilder =
  (params: URLSearchParams) =>
  <T extends string>(paramName: PageParams) =>
    (params.get(paramName) as T) || undefined;

export const useProductsQueryFilterParams = () => {
  const { productsFilter, updateProductsFilter, fetchProducts } = useProducts();
  const [isInitialised, setIsInitialised] = useState(false);
  const paramsRef = useRef<ProductFiltersWatch | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const params = paramsBuilder(searchParams);
  const groupingNameParam =
    params<ManageableProduct['groupingName']>('groupingName');
  const defaultSearchTermParam = params('defaultSearchTerm');
  const matchesParam = params('matches');
  const businessUnitIdParam = params('businessUnitId');
  const pricebookTypesParam = params('pricebookTypes');
  const parentCategoryIdParam = params('parentCategoryId');

  const urlFilterOptions = useMemo<ProductFiltersWatch>(
    () => ({
      names: splitAndSort(defaultSearchTermParam),
      matches: splitAndSort(matchesParam),
      pricebookTypes: splitAndSort(pricebookTypesParam),
      businessUnitIds: splitAndSort(businessUnitIdParam),
      categoryIds: splitAndSort(parentCategoryIdParam),
    }),
    [
      businessUnitIdParam,
      defaultSearchTermParam,
      matchesParam,
      parentCategoryIdParam,
      pricebookTypesParam,
    ]
  );

  const applyFilterOptions = useCallback(
    (filterOptions: ProductFiltersWatch, signal: AbortSignal) => {
      if (filterOptions.names.length || filterOptions.categoryIds.length) {
        fetchProducts(filterOptions, { signal });
      } else {
        updateProductsFilter(filterOptions, { reset: true });
      }
    },
    [fetchProducts, updateProductsFilter]
  );

  useEffect(() => {
    const controller = new AbortController();

    if (isFilterValid(urlFilterOptions)) {
      applyFilterOptions(urlFilterOptions, controller.signal);
    }
    setIsInitialised(true);

    return () => controller.abort('Refreshing useProductsQueryFilterParams');
  }, [applyFilterOptions, urlFilterOptions]);

  useEffect(() => {
    if (!isInitialised) {
      return;
    }

    const filterOptions: ProductFiltersWatch = {
      names: productsFilter.names,
      matches: productsFilter.matches,
      businessUnitIds: productsFilter.businessUnitIds,
      pricebookTypes: productsFilter.pricebookTypes,
      categoryIds: productsFilter.categoryIds,
    };

    if (
      !isFilterValid(filterOptions) ||
      JSON.stringify(paramsRef.current) === JSON.stringify(filterOptions)
    ) {
      return;
    }

    const ignoreDelete = !paramsRef.current;

    setSearchParams(
      (params) => {
        const update = paramsChecker(params);
        update('defaultSearchTerm', filterOptions.names, ignoreDelete);
        update('matches', filterOptions.matches, ignoreDelete);
        update('businessUnitId', filterOptions.businessUnitIds, ignoreDelete);
        update('pricebookTypes', filterOptions.pricebookTypes, ignoreDelete);
        update('parentCategoryId', filterOptions.categoryIds, ignoreDelete);

        return params;
      },
      { replace: true }
    );

    paramsRef.current = filterOptions;
  }, [
    isInitialised,
    productsFilter.businessUnitIds,
    productsFilter.categoryIds,
    productsFilter.matches,
    productsFilter.names,
    productsFilter.pricebookTypes,
    setSearchParams,
  ]);

  return {
    ...productsFilter,
    groupingName: groupingNameParam,
    isInitialised,
  };
};
